import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Check, X, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { toast } from 'react-hot-toast';

interface PasswordRequirement {
  id: string;
  label: string;
  validator: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  { id: 'length', label: 'At least 12 characters', validator: (password) => password.length >= 12 },
  { id: 'uppercase', label: 'At least one uppercase letter', validator: (password) => /[A-Z]/.test(password) },
  { id: 'lowercase', label: 'At least one lowercase letter', validator: (password) => /[a-z]/.test(password) },
  { id: 'number', label: 'At least one number', validator: (password) => /\d/.test(password) },
  { id: 'special', label: 'At least one special character', validator: (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password) },
];

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { confirmPasswordReset } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ password: '', confirmPassword: '' });

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      toast.error('Invalid or expired reset link');
      navigate('/auth/login');
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!passwordRequirements.every((req) => req.validator(form.password))) {
      setError('Please meet all password requirements');
      return;
    }

    const token = searchParams.get('token');
    if (!token) return;

    setLoading(true);
    try {
      await confirmPasswordReset(token, form.password);
      toast.success('Password reset successfully');
      navigate('/auth/login');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to reset password');
      toast.error('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(form.password);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">Reset your password</h2>
          <p className="mt-2 text-sm text-gray-600">Please enter your new password below</p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <div className="relative mt-1">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-500"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <div className="relative mt-1">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                required
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className={`h-2 rounded-full transition-all ${passwordStrength.color}`}
                style={{ width: `${passwordStrength.percentage}%` }}
              />
            </div>

            <div className="space-y-2">
              {passwordRequirements.map((req) => (
                <div key={req.id} className="flex items-center">
                  {req.validator(form.password) ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <X className="h-4 w-4 text-red-500" />
                  )}
                  <span className={`ml-2 text-sm ${
                    req.validator(form.password) ? 'text-green-700' : 'text-gray-500'
                  }`}>
                    {req.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function getPasswordStrength(password: string): { color: string; percentage: number } {
  const metRequirements = passwordRequirements.filter((req) => req.validator(password)).length;
  const percentage = (metRequirements / passwordRequirements.length) * 100;

  if (percentage === 0) return { color: 'bg-gray-200', percentage };
  if (percentage <= 20) return { color: 'bg-red-500', percentage };
  if (percentage <= 40) return { color: 'bg-orange-500', percentage };
  if (percentage <= 60) return { color: 'bg-yellow-500', percentage };
  if (percentage <= 80) return { color: 'bg-green-500', percentage };
  return { color: 'bg-green-600', percentage };
}